(() => {
  if (window.MasterCountdownProLoaded) return;
  window.MasterCountdownProLoaded = true;

  const pad = n => String(Math.max(0, n)).padStart(2, '0');

  const parseDaily = value => {
    const match = String(value || '').match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return null;
    const h = Math.min(23, Number(match[1]));
    const m = Math.min(59, Number(match[2]));
    const now = new Date();
    const end = new Date(now);
    end.setHours(h, m, 0, 0);
    if (end <= now) end.setDate(end.getDate() + 1);
    return end.getTime();
  };

  const evergreenSignature = root => [
    Math.max(1, Number(root.dataset.duration || 60)),
    root.dataset.expiry || 'zero'
  ].join('|');

  const resolveEnd = root => {
    const mode = root.dataset.mode || 'evergreen';
    if (mode === 'fixed') {
      const parsed = Date.parse(root.dataset.end || '');
      return Number.isFinite(parsed) ? parsed : 0;
    }
    if (mode === 'daily') return parseDaily(root.dataset.dailyTime);

    const minutes = Math.max(1, Number(root.dataset.duration || 60));
    const signature = evergreenSignature(root);
    const key = `master:${location.host}:${root.dataset.storageKey || 'countdown'}`;
    let stored = null;
    try {
      const raw = sessionStorage.getItem(key);
      if (raw) {
        try { stored = JSON.parse(raw); }
        catch (_) { stored = {end:Number(raw), signature:''}; }
      }
    } catch (_) {}

    let end = Number(stored?.end || 0);
    const settingChanged = stored && stored.signature !== signature;
    /* `hide_reset` arranca un ciclo nuevo en cada carga igual que `restart`.
       La diferencia entre los dos no está aquí sino en qué pasa al llegar a
       cero: `restart` reinicia el reloj a la vista y `hide_reset` recoge la
       barra. El resultado es una cuenta atrás que se agota de verdad dentro de
       la sesión —deja de presionar cuando ya no toca— y vuelve entera cuando
       el visitante llega otra vez a la página. */
    const restartsOnLoad = root.dataset.expiry === 'restart' || root.dataset.expiry === 'hide_reset';
    if (!end || settingChanged || (end <= Date.now() && restartsOnLoad)) {
      end = Date.now() + minutes * 60000;
      try { sessionStorage.setItem(key, JSON.stringify({end, signature})); } catch (_) {}
    }
    root._masterStorageKey = key;
    root._masterStorageSignature = signature;
    return end;
  };

  const storeEnd = (root, end) => {
    if (!root._masterStorageKey) return;
    try {
      sessionStorage.setItem(root._masterStorageKey, JSON.stringify({
        end,
        signature: root._masterStorageSignature || evergreenSignature(root)
      }));
    } catch (_) {}
  };

  const render = (root, end) => {
    const diff = Math.max(0, end - Date.now());
    const total = Math.floor(diff / 1000);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    root.querySelector('[data-cd-hours]')?.replaceChildren(document.createTextNode(pad(h)));
    root.querySelector('[data-cd-minutes]')?.replaceChildren(document.createTextNode(pad(m)));
    root.querySelector('[data-cd-seconds]')?.replaceChildren(document.createTextNode(pad(s)));
    return diff;
  };

  const setupScrollBehavior = root => {
    const behavior = root.dataset.behavior || 'static';
    if (behavior === 'static') return () => {};

    const placeholder = document.createElement('div');
    placeholder.className = 'master-countdown-pro__placeholder';
    placeholder.hidden = true;
    root.before(placeholder);

    let anchorTop = 0;
    let height = 0;
    let lastY = window.scrollY;
    let ticking = false;

    const measure = () => {
      const wasFixed = root.classList.contains('is-fixed');
      if (wasFixed) root.classList.remove('is-fixed');
      placeholder.hidden = true;
      const rect = root.getBoundingClientRect();
      anchorTop = rect.top + window.scrollY;
      height = rect.height;
      if (wasFixed) {
        placeholder.style.height = `${height}px`;
        placeholder.hidden = false;
        root.classList.add('is-fixed');
      }
    };

    const paint = () => {
      const y = window.scrollY;
      const shouldFix = y > anchorTop;
      root.classList.toggle('is-fixed', shouldFix);
      placeholder.hidden = !shouldFix;
      if (shouldFix) placeholder.style.height = `${height || root.offsetHeight}px`;

      if (!shouldFix) {
        root.classList.remove('is-scroll-hidden');
      } else if (behavior === 'smart') {
        if (y > lastY + 3) root.classList.add('is-scroll-hidden');
        else if (y < lastY - 3) root.classList.remove('is-scroll-hidden');
      } else if (behavior === 'sticky') {
        // Siempre visible una vez fijo: nunca se oculta al bajar.
        root.classList.remove('is-scroll-hidden');
      }
      lastY = y;
      ticking = false;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(paint);
    };
    const onResize = () => { measure(); paint(); };

    measure();
    paint();
    addEventListener('scroll', onScroll, {passive:true});
    addEventListener('resize', onResize, {passive:true});
    const ro = 'ResizeObserver' in window ? new ResizeObserver(() => { height = root.getBoundingClientRect().height || height; if (!placeholder.hidden) placeholder.style.height = `${height}px`; }) : null;
    ro?.observe(root);

    return () => {
      removeEventListener('scroll', onScroll);
      removeEventListener('resize', onResize);
      ro?.disconnect();
      placeholder.remove();
      root.classList.remove('is-fixed', 'is-scroll-hidden');
    };
  };

  const setup = root => {
    root._masterCleanup?.();
    let end = resolveEnd(root);
    if (!end) { root.hidden = true; return; }
    root.hidden = false;

    const expiry = root.dataset.expiry || 'zero';
    let timer = 0;
    let scrollCleanup = null;

    /*
      Ocultar la barra no es sólo `hidden = true`. Si estaba fijada al hacer
      scroll, `setupScrollBehavior` dejó un placeholder en el DOM reservando su
      alto; esconder la barra sin desmontarlo deja exactamente el hueco vacío
      que ocultarla pretendía evitar. Así que primero se desmonta y después se
      esconde.
    */
    const collapse = () => {
      if (timer) { clearInterval(timer); timer = 0; }
      scrollCleanup?.();
      scrollCleanup = null;
      root.hidden = true;
    };

    const tick = () => {
      const left = render(root, end);
      if (left > 0) return;
      if (expiry === 'hide' || expiry === 'hide_reset') { collapse(); return; }
      if (expiry === 'restart' && root.dataset.mode === 'evergreen') {
        end = Date.now() + Math.max(1, Number(root.dataset.duration || 60)) * 60000;
        storeEnd(root, end);
        render(root, end);
      }
      if (root.dataset.mode === 'daily') end = parseDaily(root.dataset.dailyTime) || end;
    };

    tick();
    /* Si ya venía agotada, no se monta nada: ni intervalo ni placeholder. */
    if (root.hidden) return;
    timer = setInterval(tick, 1000);
    scrollCleanup = setupScrollBehavior(root);
    root._masterCleanup = () => { if (timer) clearInterval(timer); scrollCleanup?.(); };
  };

  const init = scope => {
    const roots = scope.matches?.('[data-master-countdown-pro]')
      ? [scope]
      : [...scope.querySelectorAll?.('[data-master-countdown-pro]') || []];
    roots.forEach(setup);
  };

  init(document);
  document.addEventListener('shopify:section:load', e => init(e.target));
  document.addEventListener('shopify:section:unload', e => e.target.querySelectorAll?.('[data-master-countdown-pro]').forEach(el => el._masterCleanup?.()));
})();
